// src/components/AppImage.jsx
'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import PropTypes from 'prop-types';

function AppImage({
    src,
    alt,
    width,
    height,
    className = '',
    priority = false,
    quality = 75,
    placeholder = 'empty',
    blurDataURL,
    fill = false,
    sizes,
    onClick,
    fallbackSrc = '/assets/images/Rendalls_Competition_Logo-1762876642233.png',
    ...props
}) {
    const [imageSrc, setImageSrc] = useState(src);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    // Sync internal state when the `src` prop changes (e.g. an image
    // picker swapping a temporary blob URL for the final Storage URL
    // after upload). Without this, the inner state stays frozen at the
    // first src — and any later load failure (like a revoked blob URL)
    // permanently swaps the rendered image to the fallback logo.
    useEffect(() => {
        if (src && src !== imageSrc) {
            setImageSrc(src);
            setHasError(false);
            setIsLoading(true);
        }
    }, [src]);

    // More reliable external URL detection
    const isExternal = imageSrc?.startsWith('http://') || imageSrc?.startsWith('https://');
    const isLocal = imageSrc?.startsWith('/') || imageSrc?.startsWith('./') || imageSrc?.startsWith('data:');

    const handleError = () => {
        if (!hasError && imageSrc !== fallbackSrc) {
            setImageSrc(fallbackSrc);
            setHasError(true);
        }
        setIsLoading(false);
    };

    const handleLoad = () => {
        setIsLoading(false);
        setHasError(false);
    };

    // No pulsing skeleton during load — it permanently dims images on
    // cached/quick-loading paths where isLoading never visibly clears,
    // and the grey-200 background bleeds through transparent areas of
    // PNG product cutouts. Browsers handle the brief load gap fine on
    // their own.
    const commonClassName = `${className} ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`;

    // For external URLs or when in doubt, use regular img tag
    if (isExternal && !isLocal) {
        const imgStyle = {};

        if (width) imgStyle.width = width;
        if (height) imgStyle.height = height;

        if (fill) {
            return (
                <div className={`relative ${className}`} style={{ width: width || '100%', height: height || '100%' }}>
                    <img
                        src={imageSrc}
                        alt={alt}
                        className={`${commonClassName} absolute inset-0 w-full h-full object-cover rounded-xl`}
                        onError={handleError}
                        onLoad={handleLoad}
                        onClick={onClick}
                        style={imgStyle}
                        {...props}
                    />
                </div>
            );
        }

        return (
            <img
                src={imageSrc}
                alt={alt}
                className={`${commonClassName} rounded-xl`}
                onError={handleError}
                onLoad={handleLoad}
                onClick={onClick}
                style={imgStyle}
                {...props}
            />
        );
    }

    // For local images and data URLs, use Next.js Image component
    const imageProps = {
        src: imageSrc,
        alt,
        className: `${commonClassName} rounded-xl`,
        priority,
        quality,
        placeholder,
        blurDataURL,
        unoptimized: true,
        onError: handleError,
        onLoad: handleLoad,
        onClick,
        ...props,
    };

    if (fill) {
        return (
            <div className={`relative ${className}`}>
                <Image
                    {...imageProps}
                    fill
                    sizes={sizes || '100vw'}
                    style={{ objectFit: 'cover' }}
                />
            </div>
        );
    }

    return (
        <Image
            {...imageProps}
            width={width || 400}
            height={height || 300}
        />
    );
}

AppImage.propTypes = {
    src: PropTypes?.string?.isRequired,
    alt: PropTypes?.string?.isRequired,
    width: PropTypes?.number,
    height: PropTypes?.number,
    className: PropTypes?.string,
    priority: PropTypes?.bool,
    quality: PropTypes?.number,
    placeholder: PropTypes?.oneOf(['blur', 'empty']),
    blurDataURL: PropTypes?.string,
    fill: PropTypes?.bool,
    sizes: PropTypes?.string,
    onClick: PropTypes?.func,
    fallbackSrc: PropTypes?.string,
};

export default AppImage;